import { apiRequest } from "./client";

export type QuestionType = "single_choice" | "multi_choice" | "true_false" | "text";
export type QuizScope = "lesson" | "module" | "course";
export type QuizShowAnswers = "never" | "after_pass" | "always";

export interface OptionEditor {
  id?: number;
  position?: number;
  option_text: string;
  is_correct: boolean;
}

export interface QuestionEditor {
  id?: number;
  position?: number;
  question_type: QuestionType;
  question_text: string;
  points: number;
  explanation?: string | null;
  correct_answer_text?: string | null;
  options: OptionEditor[];
}

export interface QuizEditorDetail {
  id: number;
  title: string;
  description: string | null;
  scope: QuizScope;
  passing_score_percent: number;
  max_attempts: number | null;
  shuffle_questions: boolean;
  shuffle_options: boolean;
  time_limit_minutes: number | null;
  show_correct_answers_after: QuizShowAnswers;
  questions: QuestionEditor[];
  created_at: string;
  updated_at: string;
}

export interface QuizListItem {
  id: number;
  title: string;
  scope: QuizScope;
  passing_score_percent: number;
  questions_count: number;
  updated_at: string;
}

export interface QuizList {
  items: QuizListItem[];
  total: number;
}

export interface QuestionTake {
  id: number;
  position: number;
  question_type: QuestionType;
  question_text: string;
  points: number;
  options: { id: number; option_text: string }[];
}

export interface AttemptStartOut {
  attempt_id: number;
  quiz_id: number;
  quiz_title: string;
  started_at: string;
  time_limit_minutes: number | null;
  questions: QuestionTake[];
}

export interface AttemptFinishedQuestion {
  question_id: number;
  is_correct: boolean;
  points_awarded: number;
  max_points: number;
  correct_option_ids: number[] | null;
  correct_answer_text: string | null;
  explanation: string | null;
  your_selected_option_ids: number[] | null;
  your_text_answer: string | null;
}

export interface AttemptResult {
  attempt_id: number;
  quiz_id: number;
  finished_at: string;
  score_points: number;
  max_points: number;
  score_percent: number;
  passed: boolean;
  show_breakdown: boolean;
  breakdown: AttemptFinishedQuestion[] | null;
}

export function listQuizzes(q?: string): Promise<QuizList> {
  const u = new URLSearchParams();
  if (q) u.set("q", q);
  const s = u.toString();
  return apiRequest<QuizList>(`/quizzes${s ? `?${s}` : ""}`);
}

export function getQuiz(id: number): Promise<QuizEditorDetail> {
  return apiRequest<QuizEditorDetail>(`/quizzes/${id}`);
}

export function createQuiz(payload: {
  title: string;
  description?: string;
  scope?: QuizScope;
  passing_score_percent?: number;
  max_attempts?: number;
  shuffle_questions?: boolean;
  shuffle_options?: boolean;
  time_limit_minutes?: number;
  show_correct_answers_after?: QuizShowAnswers;
}): Promise<QuizEditorDetail> {
  return apiRequest<QuizEditorDetail>("/quizzes", { method: "POST", body: payload });
}

export function updateQuiz(
  id: number,
  payload: Partial<{
    title: string;
    description: string;
    scope: QuizScope;
    passing_score_percent: number;
    max_attempts: number | null;
    shuffle_questions: boolean;
    shuffle_options: boolean;
    time_limit_minutes: number | null;
    show_correct_answers_after: QuizShowAnswers;
  }>,
): Promise<QuizListItem> {
  return apiRequest<QuizListItem>(`/quizzes/${id}`, { method: "PATCH", body: payload });
}

export function bulkUpsertQuestions(
  quizId: number,
  questions: QuestionEditor[],
): Promise<void> {
  return apiRequest<void>(`/quizzes/${quizId}/questions`, {
    method: "PUT",
    body: { questions },
  });
}

export function deleteQuiz(id: number): Promise<void> {
  return apiRequest<void>(`/quizzes/${id}`, { method: "DELETE" });
}

// Student attempts

export function startAttempt(quizId: number): Promise<AttemptStartOut> {
  return apiRequest<AttemptStartOut>(`/quizzes/${quizId}/start`, { method: "POST" });
}

export function submitAnswer(
  attemptId: number,
  payload: { question_id: number; selected_option_ids?: number[]; text_answer?: string },
): Promise<void> {
  return apiRequest<void>(`/attempts/${attemptId}/answer`, { method: "POST", body: payload });
}

export function finishAttempt(attemptId: number): Promise<AttemptResult> {
  return apiRequest<AttemptResult>(`/attempts/${attemptId}/finish`, { method: "POST" });
}
